// SolveRearrangedQuadraticTask.js
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";
import { toTextFractionLatex } from "../../../../utils/fractionLatex";

// === UTIL ===
function gcd(a, b) {
  while (b !== 0) [a, b] = [b, a % b];
  return Math.abs(a);
}
function formatFractionLatex(value) {
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  const intPart = Math.floor(abs);
  const frac = abs - intPart;
  if (frac === 0) return `${sign}${intPart}`;
  for (let d = 1; d <= 20; d++) {
    const n = Math.round(frac * d);
    if (Math.abs(n / d - frac) < 0.01) {
      const g = gcd(n, d);
      const num = n / g;
      const den = d / g;
      return intPart === 0
        ? `${sign}\\tfrac{${num}}{${den}}`
        : `${sign}${intPart}\\tfrac{${num}}{${den}}`;
    }
  }
  return `${sign}${abs.toFixed(2)}`;
}

// Format pojedynczego wyrazu z ukryciem współczynnika 1 przy x^2 i x
function termLatex(coef, variable, isFirst) {
  if (coef === 0) return "";
  const sign = coef < 0 ? "-" : "+";
  const abs = Math.abs(coef);
  const body = variable
    ? (abs === 1 ? `${variable}` : `${abs}${variable}`)
    : `${abs}`;
  return isFirst ? (coef < 0 ? `-${body}` : `${body}`) : ` ${sign} ${body}`;
}
// Buduje całe wyrażenie po jednej stronie
function sideFromCoeffs({ x2 = 0, x = 0, c = 0 }) {
  const parts = [];
  const push = (coef, variable) => {
    const isFirst = parts.length === 0;
    const t = termLatex(coef, variable, isFirst);
    if (t) parts.push(t);
  };
  push(x2, "x^2");
  push(x, "x");
  push(c, "");
  return parts.length ? parts.join("") : "0";
}

// === GENERATE ===
export function generateTask() {
  let a, b, c, delta, sqrtDelta, x1, x2;

  // Losujemy docelowe a,b,c (forma standardowa), wymagamy Δ>0 i pierwiastek całkowity
  while (true) {
    a = (Math.random() < 0.5 ? -1 : 1) * (Math.floor(Math.random() * 3) + 1);      // ±1..±3
    b = (Math.random() < 0.5 ? -1 : 1) * (Math.floor(Math.random() * 5) + 1);      // ±1..±5
    c = (Math.random() < 0.5 ? -1 : 1) * (Math.floor(Math.random() * 7) + 1);      // ±1..±7

    if (a === 0) continue;
    delta = b * b - 4 * a * c;
    sqrtDelta = Math.sqrt(delta);
    if (delta > 0 && Number.isInteger(sqrtDelta)) {
      const denom = 2 * a;
      x1 = (-b + sqrtDelta) / denom;
      x2 = (-b - sqrtDelta) / denom;
      if (Math.abs(x1) <= 20 && Math.abs(x2) <= 20) break;
    }
  }

  // Z formy standardowej budujemy „porozrzucane” równanie:
  // każdy z trzech składników losowo przenosimy na prawą stronę (ze zmianą znaku),
  // pilnując, by obie strony nie były puste.
  let moveA = Math.random() < 0.5;
  let moveB = Math.random() < 0.5;
  let moveC = Math.random() < 0.5;
  if (!moveA && !moveB && !moveC) moveA = true;                 // prawa strona nie może być pusta
  if (moveA && moveB && moveC) moveC = false;                    // lewa też nie może być pusta

  const leftCoeffs  = { x2: moveA ? 0 : a, x: moveB ? 0 : b, c: moveC ? 0 : c };
  const rightCoeffs = { x2: moveA ? -a : 0, x: moveB ? -b : 0, c: moveC ? -c : 0 };

  const originalLatex = `${sideFromCoeffs(leftCoeffs)} = ${sideFromCoeffs(rightCoeffs)}`;
  const standardLatex = `${sideFromCoeffs({ x2: a, x: b, c })} = 0`; // bez „1” przy x^2 i x

  return {
    type: "SolveRearrangedQuadraticTask",
    data: {
      a, b, c, delta, sqrtDelta, x1, x2,
      originalLatex,
      standardLatex
    }
  };
}

// === RENDER TASK ===
export function renderTask({ data }) {
  return (
    <p>
      Rozwiąż równanie: <InlineMath math={data.originalLatex} />
    </p>
  );
}

// === INPUT ===
export function renderInput(_, userInput, onChange, disabled) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span><InlineMath math={'x_1 ='} /></span>
        <FractionInput value={userInput?.x1 || ""} onChange={val => onChange({ ...userInput, x1: val })} disabled={disabled} label="x₁" />
      </div>
      <div className="flex items-center gap-2">
        <span><InlineMath math={'x_2 ='} /></span>
        <FractionInput value={userInput?.x2 || ""} onChange={val => onChange({ ...userInput, x2: val })} disabled={disabled} label="x₂" />
      </div>
    </div>
  );
}

// === CHECK ANSWER ===
export function checkAnswer({ data }, userInput) {
  const u1 = userInput?.x1?.parsed;
  const u2 = userInput?.x2?.parsed;
  if ([u1, u2].some(v => v == null || isNaN(v))) return { correct: false };
  const { x1, x2 } = data;
  const correct =
    (Math.abs(u1 - x1) < 0.01 && Math.abs(u2 - x2) < 0.01) ||
    (Math.abs(u1 - x2) < 0.01 && Math.abs(u2 - x1) < 0.01);
  return { correct, expected: [x1, x2] };
}

// === KRÓTKA ODPOWIEDŹ (UI) — lewo + odstępy ===
export function renderAnswer({ data }) {
  return (
    <div style={{ textAlign: "left", marginTop: "0.5rem" }}>
      <div><InlineMath math={`x_1 = ${toTextFractionLatex(formatFractionLatex(data.x1))}`} /></div>
      <div style={{ marginTop: "0.5rem" }}>
        <InlineMath math={`x_2 = ${toTextFractionLatex(formatFractionLatex(data.x2))}`} />
      </div>
    </div>
  );
}

// === WYJAŚNIENIE ===
export function renderExplanation({ data }) {
  const { a, b, c, delta, sqrtDelta, x1, x2, originalLatex, standardLatex } = data;
  const denom = 2 * a;

  // KLUCZ: poprawny zapis -b w LaTeX (dla b<0 dostajemy -(-2), a nie -(2))
  const minusBLatex = b < 0 ? `-(${b})` : `-${b}`;

  // Pomocnicze formaty ułamków do ładnego podsumowania
  const fmt = (v) => formatFractionLatex(v);

  // Bloki obliczeń x1 / x2 (bez zbędnych „pół-symbolicznych” linii)
  const x1Lines = String.raw`
\begin{aligned}
x_1 &= \dfrac{-b + \sqrt{\Delta}}{2a} \\[4pt]
x_1 &= \dfrac{${minusBLatex} + ${sqrtDelta}}{2\cdot ${a}} \\[4pt]
x_1 &= \dfrac{${-b + sqrtDelta}}{${denom}} \\[4pt]
x_1 &= ${fmt(x1)}
\end{aligned}
`;
  const x2Lines = String.raw`
\begin{aligned}
x_2 &= \dfrac{-b - \sqrt{\Delta}}{2a} \\[4pt]
x_2 &= \dfrac{${minusBLatex} - ${sqrtDelta}}{2\cdot ${a}} \\[4pt]
x_2 &= \dfrac{${-b - sqrtDelta}}{${denom}} \\[4pt]
x_2 &= ${fmt(x2)}
\end{aligned}
`;

  return (
    <div className="space-y-4">
      <p><strong>Krok 1:</strong> Przenosimy wszystko na jedną stronę i redukujemy:</p>
      <BlockMath math={`${originalLatex} \\Rightarrow ${standardLatex}`} />

      <p><strong>Krok 2:</strong> Współczynniki i delta:</p>
      <BlockMath math={`a = ${a},\\quad b = ${b},\\quad c = ${c}`} />
      <BlockMath math={`\\Delta = b^2 - 4ac`} />
      <BlockMath math={`\\Delta = (${b})^2 - 4 \\cdot ${a} \\cdot ${c} = ${delta}`} />
      <BlockMath math={`\\sqrt{\\Delta} = ${sqrtDelta}`} />

      <p><strong>Krok 3:</strong> Obliczamy pierwiastki:</p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: "1rem",
          alignItems: "start",
        }}
      >
        <div>
          <div style={{ textAlign: "center", fontWeight: 600 }}>
            <InlineMath math={'x_1'} />
          </div>
          <BlockMath math={x1Lines} />
        </div>
        <div>
          <div style={{ textAlign: "center", fontWeight: 600 }}>
            <InlineMath math={'x_2'} />
          </div>
          <BlockMath math={x2Lines} />
        </div>
      </div>

      <p><strong>Odpowiedź końcowa:</strong></p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={`x_1 = ${fmt(x1)},\\quad x_2 = ${fmt(x2)}`} />
      </div>
    </div>
  );
}


// === ODPOWIEDŹ UŻYTKOWNIKA ===
export function renderUserAnswer(userInput) {
  if (!userInput?.x1?.raw || !userInput?.x2?.raw) {
    return <i>(brak odpowiedzi)</i>;
  }
  const x1 = formatLatexPreview(userInput.x1.raw, "x_1");
  const x2 = formatLatexPreview(userInput.x2.raw, "x_2");
  return (
    <div style={{ marginTop: "0.5rem", textAlign: "left" }}>
      <div><InlineMath math={x1} /></div>
      <div style={{ marginTop: "0.5rem" }}>
        <InlineMath math={x2} />
      </div>
    </div>
  );
}
